import React, { useContext, useState, useEffect } from 'react'
import styled, { css } from 'styled-components'
import i18n from 'live/services/i18n'
import { getColorFromResult, RATINGS_COLORS, RATINGS_TEXT } from '../common/utils'
import { ResultContext } from './qualityAnalysis'
import { getDeviationsInfo } from './ratingBoxDeviationInfo'
import { ParamsContext } from '../qualityMonitoring/qualityMonitoring'
import { getProduct } from '../common/apis'
import { motion } from 'framer-motion'
import { resultsVariants } from './analysisResult'

export function ResultSummary(): JSX.Element {
    const urlParams = useContext(ParamsContext).split('/')
    // TODO: get sku color from generalInfo
    const { generalInfo, ratingInfo, finalScore, analysisType, activeRatings } = useContext(ResultContext)

    const [imageId, setImageId] = useState(undefined)
    const [skuColor, setSkuColor] = useState(undefined)

    useEffect(() => {
        const productName = generalInfo.product

        getProduct(productName)
            .then(product => {
                const { productImageId } = product
                setImageId(productImageId)
                const newSkuColor = product.SKUs.filter(sku => sku.skuName === generalInfo.sku)[0].skuColor
                setSkuColor(newSkuColor)
            })
            .catch(() => {
                setImageId(undefined)
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlParams])

    const logo = imageId ? <img src={`/services/plugin-upload/download/${imageId}`} alt="logo" /> : null

    return (
        <StyledResultSummary
            className={'resultSummary'}
            style={
                {
                    '--finalResultColor': getColorFromResult(finalScore),
                    '--finalResult': finalScore + '%',
                    '--skuColor': skuColor
                } as React.CSSProperties
            }
            variants={resultsVariants}
            initial={'hidden'}
            animate={'visible'}
            exit={'hidden'}
        >
            <div className="resultSummary--title">{i18n('grades')}</div>
            <div className="resultSummary--finalResult">
                <div className="resultSummary--finalResult__text">
                    <span>
                        <strong>{finalScore}</strong>%
                    </span>
                    <span>{i18n('final result')}</span>
                </div>

                <div className="resultSummary--finalResult__logo" style={{}}>
                    {logo}
                </div>
            </div>
            <div className="resultSummary--resultLevelBar">
                <span className="resultSummary--resultLevelBar__achieved" />
            </div>

            <div className="resultSummary--weighings">
                <span className="resultSummary--weighings__title">{i18n('weighings')}</span>
                <div className="resultSummary--weighings__content">
                    <div className="resultSummary--weighings__content--grade">
                        <span
                            className={`resultSummary--weighings__content--grade__number ${
                                RATINGS_TEXT[generalInfo.weighingScore]
                            }`}
                        >
                            {generalInfo.weighingScore}
                        </span>
                        <span className="resultSummary--weighings__content--grade__text">
                            {RATINGS_TEXT[generalInfo.weighingScore]}
                        </span>
                    </div>
                    <Separator />
                    <div className="resultSummary--weighings__content--weights">
                        {generalInfo.weighings.map((weighing, index) => (
                            <div className="resultSummary--weighings__content--weights__weight" key={index}>
                                <span className="resultSummary--weighings__content--weights__weight--title">
                                    {i18n('weight')} {index + 1}
                                </span>
                                <span className="resultSummary--weighings__content--weights__weight--value">
                                    {`${weighing.weight.toString().replace('.', ',')} / ${weighing.grammage}`}
                                </span>
                            </div>
                        ))}
                        {analysisType === 'icc' ? (
                            <div className="resultSummary--weighings__content--weights__average">
                                <span className="resultSummary--weighings__content--weights__average--title">
                                    {i18n('average')}
                                </span>
                                <span className="resultSummary--weighings__content--weights__average--value">
                                    {generalInfo.weighingAverage.toString().replace('.', ',')}
                                </span>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
            {activeRatings.map((ratingType: RatingTypes) => {
                const deviationsInfo = getDeviationsInfo(ratingType, ratingInfo as RatingInfo)

                return (
                    <div key={ratingType} className="resultSummary--rating">
                        <span className="resultSummary--rating__title">{i18n(ratingType)}</span>
                        <div className="resultSummary--rating__content">
                            <div className="resultSummary--rating__content--grade">
                                <span
                                    className={`resultSummary--rating__content--grade__number ${
                                        RATINGS_TEXT[ratingInfo[ratingType].score]
                                    }`}
                                >
                                    {ratingInfo[ratingType].score}
                                </span>
                                <span className="resultSummary--rating__content--grade__text">
                                    {RATINGS_TEXT[ratingInfo[ratingType].score]}
                                </span>
                            </div>
                            <Separator />

                            <div className="resultSummary--rating__content--deviations">
                                <span className="resultSummary--rating__content--deviations__title">
                                    {i18n('deviations')}
                                </span>
                                <div className="resultSummary--rating__content--deviations__text">
                                    {![...ratingInfo[ratingType].deviations.allDeviations].length ? (
                                        <strong>{i18n('none')}</strong>
                                    ) : (
                                        Object.keys(deviationsInfo).map(packager => {
                                            if (packager === 'deviations') {
                                                return <strong>{[...deviationsInfo.deviations].join(', ')}</strong>
                                            } else if (deviationsInfo[packager]) {
                                                return (
                                                    <span>
                                                        {packager}: &nbsp;
                                                        <strong>
                                                            {[...deviationsInfo[packager]].join(', ')}; &nbsp;
                                                        </strong>
                                                    </span>
                                                )
                                            }
                                        })
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            })}
        </StyledResultSummary>
    )
}

const Separator = styled.div`
    width: 2px;
    align-self: stretch;
    background: ${({ theme }) => theme['gray-300']};
`

const StyledResultSummary = styled(motion.div)`
    border: 1px solid ${({ theme }) => theme['gray-300']};
    border-radius: 3px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    --padding: 10px 24px;
    margin-bottom: 100px;
    @media (max-width: 1100px) {
        margin-bottom: 45px;
    }
    .resultSummary {
        &--title {
            text-transform: uppercase;
            font-size: 14px;
            padding: 12px 24px;
            border-bottom: 1px solid ${({ theme }) => theme['gray-300']};
            font-family: ${({ theme }) => theme['font-semibold']};
        }
        &--finalResult {
            padding: var(--padding);
            display: flex;
            justify-content: space-between;
            align-items: center;
            position: relative;
            &__logo {
                width: 80px;
                height: 80px;
                background: var(--skuColor);
                display: flex;
                align-items: center;
                justify-content: center;
                position: absolute;
                right: 30px;
                bottom: 0;
                img {
                    width: 100%;
                    max-width: 70px;
                    max-height: 70px;
                }
            }

            &__text {
                font-size: 16px;
                font-family: ${({ theme }) => theme['font-semibold']};
                width: 130px;
                word-break: break-word;
                text-transform: uppercase;
                display: flex;
                flex-direction: column;
                strong {
                    font-size: 50px;
                    line-height: 50px;
                    color: var(--finalResultColor);
                }
            }
        }
        &--resultLevelBar {
            height: 10px;
            background: ${({ theme }) => theme['gray-300']};
            position: relative;
            &__achieved {
                position: absolute;
                top: 0;
                left: 0;
                width: var(--finalResult);
                height: 100%;
                background: var(--finalResultColor);
                border-top-right-radius: 10px;
                border-bottom-right-radius: 10px;
            }
        }
        &--weighings,
        &--rating {
            padding: var(--padding);
            display: flex;
            flex-direction: column;
            align-items: stretch;
            border-top: 1px solid ${({ theme }) => theme['gray-300']};

            &__title {
                text-transform: uppercase;
                font-size: 12px;
                font-family: ${({ theme }) => theme['font-semibold']};
                margin-bottom: 10px;
            }
            &__content {
                display: grid;
                grid-template-columns: 20% 1px auto;
                grid-column-gap: 22px;
                align-items: center;
                &--grade {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    width: 50px;
                    &__number {
                        font-size: 24px;
                        font-family: ${({ theme }) => theme['font-semibold']};
                        ${() =>
                            css`
                                ${RATINGS_TEXT.map(
                                    (rating, index) =>
                                        `
                                            &.${rating} {
                                                color: ${RATINGS_COLORS[index]};
                                            }
                                        `
                                ).join()}
                            `}
                    }
                    &__text {
                        font-size: 14px;
                        text-transform: capitalize;
                        margin-top: 5px;
                    }
                }
            }
        }
        &--weighings {
            &__content {
                &--weights {
                    display: flex;
                    align-items: center;
                    &__weight,
                    &__average {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        margin-right: 10px;
                        &--title {
                            font-size: 12px;
                            text-transform: capitalize;
                        }
                        &--value {
                            font-size: 13px;
                            font-family: ${({ theme }) => theme['font-semibold']};
                        }
                    }
                }
            }
        }
        &--rating {
            &__content {
                &--deviations {
                    &__title {
                        font-size: 12px;
                        text-transform: capitalize;
                    }
                    &__text {
                        text-transform: capitalize;
                    }
                }
            }
        }
    }
`
